import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import { Collapse } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUser } from '../../../../hooks/use_user';
import { ProUpsellModalActions } from '../../../../redux/pro_upsell_modal';
import { SignupLoginModalActions } from '../../../../redux/signup_login_modal';
import { RoutePaths } from '../../../../routes';
import {
  PRO_SIGNUP_LOGIN_MODAL_OPEN_PARAMS,
  PRO_TRIAL_SIGNUP_LOGIN_MODAL_OPEN_PARAMS,
} from '../../../login_signup_modal/signup_login_modal';
import navionicsImg from '../../../map/layer_detail_images/navionics.jpg';
import { OmniaLinkButton } from '../../../omnia_button/omnia_link_button';
import SvgLockIcon from '../../../svg/lock_icon';
import SvgNavionicsIcon from '../../../svg/navionics_icon';
import {
  CORE_LAYER,
  CORE_LAYER_INFO,
  LayerInfo,
  LAYER_TYPES,
  MapLayerState,
  OVERLAY_LAYER,
  OVERLAY_LAYER_INFO,
  POINTS_OF_INTEREST_LAYER,
  POINTS_OF_INTEREST_LAYER_INFO,
  WEATHER_LAYER,
  WEATHER_LAYER_INFO,
} from '../../map_types';
import { FooterCard } from './footer_card';
import { LayersDetailCard } from './layers_detail_card';
import styles from './layers_modal.less';
import { LayerCard } from './layer_card';
import layerCardStyles from './layer_card.less';
import { UpgradeCard } from './upgrade_card';
interface MapLayersPickerProps {
  isModalOpen: boolean;
  mapLayerState: MapLayerState;
  onCoreLayerChange: (layer: CORE_LAYER) => void;
  onOverlayLayersChange: (layer: OVERLAY_LAYER) => void;
  onSelectPointsOfInterestLayer: (layer: POINTS_OF_INTEREST_LAYER) => void;
  onWeatherLayerChange: (weather: WEATHER_LAYER) => void;
  onModalChange: (value: boolean) => void;
}

enum PanelKeys {
  POINTS_OF_INTEREST = 'poi',
  CORE = 'core',
  WEATHER = 'weather',
}

export const isStandardLayer = (layer: LayerInfo) => {
  return (
    layer.layerType === LAYER_TYPES.BASE ||
    layer.layerType === LAYER_TYPES.POINTS_OF_INTEREST ||
    layer.layerType === LAYER_TYPES.OVERLAY
  );
};

export const LayersModal = React.memo((props: MapLayersPickerProps) => {
  const {
    isModalOpen,
    mapLayerState,
    onCoreLayerChange,
    onModalChange,
    onOverlayLayersChange,
    onSelectPointsOfInterestLayer,
    onWeatherLayerChange,
  } = props;
  const modalRef = useRef<HTMLDivElement>();
  const [selectedLayerInfo, setSelectedLayerInfo] = useState<LayerInfo>();
  const { isPro, user } = useUser();
  const noUser = !user;
  const isNotPro = !isPro;
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();

  const initialActivePanels = [PanelKeys.POINTS_OF_INTEREST, PanelKeys.CORE, PanelKeys.WEATHER];
  const [activePanels, setActivePanels] = useState<PanelKeys[]>(initialActivePanels);
  const { Panel } = Collapse;

  const onLayersDetailsSelect = useCallback(
    ({ layerType, value }: LayerInfo) => {
      switch (layerType) {
        case LAYER_TYPES.OVERLAY:
          onOverlayLayersChange(value as OVERLAY_LAYER);
          return;
        case LAYER_TYPES.WEATHER:
          onWeatherLayerChange(value as WEATHER_LAYER);
          return;
        case LAYER_TYPES.POINTS_OF_INTEREST:
          onSelectPointsOfInterestLayer(value as POINTS_OF_INTEREST_LAYER);
          return;
        case LAYER_TYPES.CORE:
          onCoreLayerChange(value as CORE_LAYER);
          return;
      }
    },
    [onOverlayLayersChange, onWeatherLayerChange, onSelectPointsOfInterestLayer, onCoreLayerChange]
  );

  const isLayerLocked = useCallback(
    (layer: LayerInfo) => {
      const isFreeLayer = isStandardLayer(layer);
      const isPremiumLayer = !isFreeLayer;
      const layerIsWaypoints = layer.value === POINTS_OF_INTEREST_LAYER.WAYPOINTS;

      if (isFreeLayer && layerIsWaypoints) {
        return noUser;
      }
      if (isFreeLayer) {
        return false;
      }
      return isPremiumLayer && !isPro;
    },
    [user, isPro]
  );

  const onLockedLayerClick = (trial = false) => {
    if (noUser) {
      const params = trial
        ? PRO_TRIAL_SIGNUP_LOGIN_MODAL_OPEN_PARAMS
        : PRO_SIGNUP_LOGIN_MODAL_OPEN_PARAMS;
      dispatch(SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN(params));
      if (isMobile) {
        onModalChange(false);
      }
    } else if (isNotPro) {
      dispatch(ProUpsellModalActions.PRO_UPSELL_MODAL_OPEN());
      if (isMobile) {
        onModalChange(false);
      }
    }
  };

  const orderedCoreLayers = _.orderBy(Object.values(CORE_LAYER_INFO), ['order'], ['asc']);
  const orderedWeatherLayers = _.orderBy(Object.values(WEATHER_LAYER_INFO), ['order'], ['asc']);
  const pointsOfInterestLayers = Object.values(POINTS_OF_INTEREST_LAYER_INFO);

  if (!isModalOpen) {
    return null;
  }

  return (
    <div className={styles.modal} ref={modalRef}>
      <div className={styles.modalContent}>
        {selectedLayerInfo ? (
          <LayersDetailCard
            onLockedLayerClick={onLockedLayerClick}
            onLayersDetailsSelect={onLayersDetailsSelect}
            onModalChange={onModalChange}
            mapLayerState={mapLayerState}
            selectedLayerInfo={selectedLayerInfo}
            onSelectedLayerInfoChange={setSelectedLayerInfo}
            isLayerLocked={isLayerLocked}
          />
        ) : (
          <>
            <div className={styles.modalHeader}>
              <span className={styles.heading}>All Layers</span>
              <CloseOutlined onClick={() => onModalChange(false)} />
            </div>
            <Collapse
              expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
              expandIconPosition="end"
              onChange={(panels) => {
                setActivePanels(panels as PanelKeys[]);
              }}
              activeKey={activePanels}
              destroyInactivePanel
              ghost
            >
              <Panel
                key={PanelKeys.POINTS_OF_INTEREST}
                header={<span className={styles.layerHeading}>Points of Interest</span>}
              >
                <section>
                  <ul className={styles.layerCardsList}>
                    <li>
                      <LayerCard
                        layer={OVERLAY_LAYER_INFO[OVERLAY_LAYER.PINS]}
                        checked={mapLayerState[LAYER_TYPES.OVERLAY].includes(OVERLAY_LAYER.PINS)}
                        onSelect={() =>
                          onOverlayLayersChange(
                            OVERLAY_LAYER_INFO[OVERLAY_LAYER.PINS].value as OVERLAY_LAYER
                          )
                        }
                        onDetailsClick={() =>
                          setSelectedLayerInfo(OVERLAY_LAYER_INFO[OVERLAY_LAYER.PINS])
                        }
                        isLocked={isLayerLocked(OVERLAY_LAYER_INFO.PINS)}
                      />
                    </li>

                    {pointsOfInterestLayers.map((poiLayer) => {
                      return (
                        <li key={poiLayer.value}>
                          <LayerCard
                            layer={poiLayer}
                            checked={mapLayerState[LAYER_TYPES.POINTS_OF_INTEREST].includes(
                              poiLayer.value as POINTS_OF_INTEREST_LAYER
                            )}
                            onSelect={() =>
                              onSelectPointsOfInterestLayer(
                                poiLayer.value as POINTS_OF_INTEREST_LAYER
                              )
                            }
                            onDetailsClick={() => setSelectedLayerInfo(poiLayer)}
                            isLocked={isLayerLocked(poiLayer)}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </Panel>
              <UpgradeCard onLockedLayerClick={onLockedLayerClick} />
              <Panel
                key={PanelKeys.CORE}
                header={<span className={styles.layerHeading}>Water Layers</span>}
              >
                <section>
                  <ul className={styles.layerCardsList}>
                    <li className="px-2.5 py-3">
                      <div className="flex items-center">
                        <span className={classNames(styles.layerIcon, 'relative block')}>
                          <img src={navionicsImg} alt="" className="rounded-lg" />
                          <span className="w-8 h-8 bg-[#11181C] rounded-full p-[.3rem] flex items-center justify-center absolute">
                            <SvgNavionicsIcon color={'#657D7D'} className="w-6" />
                          </span>
                        </span>
                        <span className={layerCardStyles.cardInfo}>
                          <span className={layerCardStyles.layerName}>
                            Navionics HD Depth Charts
                          </span>
                          <span className={styles.premProLine}>
                            Available only in the
                            <OmniaLinkButton
                              to={RoutePaths.APP}
                              kind="link"
                              size="md"
                              className={styles.premProLink}
                            >
                              Omnia App
                            </OmniaLinkButton>
                          </span>
                        </span>
                        <span className={layerCardStyles.lockIcon}>
                          <SvgLockIcon />
                        </span>
                      </div>
                    </li>

                    {orderedCoreLayers.map((coreLayer) => {
                      return (
                        <li key={coreLayer.value}>
                          <LayerCard
                            layer={coreLayer}
                            checked={mapLayerState[LAYER_TYPES.CORE] === coreLayer.value}
                            onSelect={() => {
                              if (noUser || isNotPro) {
                                onLockedLayerClick();
                                return;
                              }
                              onCoreLayerChange(coreLayer.value as CORE_LAYER);
                            }}
                            onDetailsClick={() => setSelectedLayerInfo(coreLayer)}
                            useButton
                            isLocked={isLayerLocked(coreLayer)}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </Panel>
              <Panel
                key={PanelKeys.WEATHER}
                header={<span className={styles.layerHeading}>Weather Layers</span>}
              >
                <section>
                  <ul className={styles.layerCardsList}>
                    {orderedWeatherLayers.map((weatherLayer) => (
                      <li key={weatherLayer.name}>
                        <LayerCard
                          layer={weatherLayer}
                          checked={mapLayerState[LAYER_TYPES.WEATHER].includes(
                            weatherLayer.value as WEATHER_LAYER
                          )}
                          onSelect={() => {
                            if (noUser || isNotPro) {
                              onLockedLayerClick();
                              return;
                            }
                            onWeatherLayerChange(weatherLayer.value as WEATHER_LAYER);
                          }}
                          onDetailsClick={() => setSelectedLayerInfo(weatherLayer)}
                          isLocked={isLayerLocked(weatherLayer)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              </Panel>
              <FooterCard />
            </Collapse>
          </>
        )}
      </div>
    </div>
  );
});
LayersModal.displayName = 'LayersModal';
